import { IdGenerator } from '@src/editor/services/IdGenerator';

import type { NameSource, Reel, ReelChapter } from './reelTypes';

/**
 * PLAT-11055 — chapters in the stored reel (DE12).
 *
 * A chapter is a *boundary*: it owns the Sections from its own `firstSectionId`
 * down to the next chapter's. Membership is never stored, only derived from the
 * timeline's order (DE3) — so a reorder needs no chapter bookkeeping at all.
 *
 * All pure and non-mutating; the caller writes the returned reel into
 * `clipJson.reel`, which is handed around by reference.
 *
 * **Every writer here spreads `...reel` before overriding its own fields.** They
 * were written when a reel was exactly `{ chapters, sections }` and each rebuilt
 * that pair from scratch, so PLAT-11072's `disabledSections` was silently
 * dropped by all of them — a Section turned off vanished the moment any chapter
 * was renamed, collapsed or dragged into, because an off Section has no timeline
 * elements to fall back on either. `reelWritersKeepDisabled.test.ts` locks the
 * invariant in for every writer, this file's and the others'.
 */

export type ReelChapterGroup<TRow> = {
  chapter: ReelChapter;
  /** 1-based position — also an unnamed chapter's rendered title (DS5). */
  ordinal: number;
  rows: TRow[];
};

/** A fresh boundary: no title, and the marker that says a title may replace it. */
function newChapter(
  anchor: Pick<ReelChapter, 'firstSectionId' | 'afterSectionId'>,
): ReelChapter {
  return {
    id: IdGenerator.shortRandomId(),
    title: '',
    titleSource: 'clip',
    ...anchor,
  };
}

/** A chapter resolved to a position in the row order. */
type PlacedChapter = {
  chapter: ReelChapter;
  boundary: number;
  /** Owns nothing: it is a gap held open, not a span (PLAT-11075 DS3). */
  isEmpty: boolean;
  /** Position in `reel.chapters`, which orders two empties in one gap. */
  order: number;
};

/**
 * Groups the reel's rows under their chapters, in playback order. Empty when
 * the reel has no chapters — the flat list stays the default rendering (DS2).
 *
 * A chapter resolves to a *position* in the row order: the Section it opens
 * at, or — for one that owns nothing — the slot after the Section it is
 * anchored behind. That second form is how an empty chapter both renders as a
 * holder now and picks up whatever Sections land in it later, with no write of
 * its own when they do.
 *
 * Two boundaries can share a position, because an empty chapter and the chapter
 * that owns the Section below it are in the same gap (PLAT-11075 DS3). The
 * empty one renders first — it is the gap, and the other is what comes after
 * it — and a span always runs to the next boundary that *owns* something, so an
 * empty never cuts the chapter above it short.
 */
export function deriveReelChapterGroups<TRow extends { id: string }>(
  rows: TRow[],
  chapters: ReelChapter[] | undefined,
): ReelChapterGroup<TRow>[] {
  if (!chapters?.length || !rows.length) return [];

  const indexOf = new Map(rows.map((row, index) => [row.id, index]));

  const owning: PlacedChapter[] = [];
  const empties: PlacedChapter[] = [];
  const unplaceable: ReelChapter[] = [];
  chapters.forEach((chapter, order) => {
    const opensAt = indexOf.get(chapter.firstSectionId);
    if (opensAt !== undefined) {
      owning.push({ chapter, boundary: opensAt, isEmpty: false, order });
      return;
    }
    if (chapter.afterSectionId !== undefined) {
      // `''` is the gap at the top of the reel (DS4). An anchor that has left
      // the reel does not cost the chapter its place — it falls back to the
      // end, which is where such a chapter was originally put.
      const follows =
        chapter.afterSectionId === ''
          ? -1
          : (indexOf.get(chapter.afterSectionId) ?? rows.length - 1);
      empties.push({ chapter, boundary: follows + 1, isEmpty: true, order });
      return;
    }
    unplaceable.push(chapter);
  });

  // Two owning chapters on one boundary merge, first one wins. DS6 keeps the UI
  // from offering that, so this is the derivation's own safeguard rather than
  // something to repair after the fact.
  const byBoundary = new Map<number, PlacedChapter>();
  for (const placed of owning) {
    if (!byBoundary.has(placed.boundary))
      byBoundary.set(placed.boundary, placed);
  }

  // DS3's invariant: the reel's first Section always opens a chapter, so no
  // Section is left ahead of every boundary — a state DE12 has no slot for.
  // The opener is a chapter whose own Section has since left the reel (it
  // advances to the Section that now comes first); failing that, whichever
  // chapter currently sits earliest moves up to meet the top.
  if (!byBoundary.has(0)) {
    const [advancing] = unplaceable;
    if (advancing) {
      byBoundary.set(0, {
        chapter: advancing,
        boundary: 0,
        isEmpty: false,
        order: chapters.indexOf(advancing),
      });
    } else if (byBoundary.size > 0) {
      const earliest = Math.min(...byBoundary.keys());
      const moved = byBoundary.get(earliest);
      if (moved) {
        byBoundary.delete(earliest);
        byBoundary.set(0, { ...moved, boundary: 0 });
      }
    } else {
      // Every chapter is empty, yet there are Sections to place: the earliest
      // takes the top rather than leaving them outside every chapter.
      const [first] = empties.splice(0, 1);
      if (first) {
        byBoundary.set(0, { ...first, boundary: 0, isEmpty: false });
      }
    }
  }

  const spans = [...byBoundary.keys()].sort((a, b) => a - b);
  const placed = [...empties, ...byBoundary.values()].sort(
    (a, b) =>
      a.boundary - b.boundary ||
      // In one gap, what owns nothing comes before what owns the rows below it.
      Number(b.isEmpty) - Number(a.isEmpty) ||
      a.order - b.order,
  );

  return placed.map(({ chapter, boundary, isEmpty }, index) => ({
    chapter,
    ordinal: index + 1,
    rows: isEmpty
      ? []
      : rows.slice(
          boundary,
          spans.find(next => next > boundary) ?? rows.length,
        ),
  }));
}

/**
 * Opens a chapter at `sectionId`. Inserting the *first* boundary below the top
 * also opens one at the reel's first Section, so Sections above it are not
 * orphaned outside every chapter (DS3).
 *
 * A Section that already starts a chapter is left alone — DS6 hides the
 * affordance there, and a second boundary on one Section is not representable.
 */
export function insertChapterInReel(
  reel: Reel | undefined,
  sectionId: string,
  firstSectionId: string,
): Reel {
  const chapters = reel?.chapters ?? [];
  if (chapters.some(chapter => chapter.firstSectionId === sectionId)) {
    return {
      ...reel,
      chapters: [...chapters],
      sections: { ...reel?.sections },
    };
  }

  return withChapter(
    reel,
    newChapter({ firstSectionId: sectionId }),
    // Nothing to orphan when the new boundary is itself the reel's top.
    sectionId === firstSectionId ? undefined : firstSectionId,
  );
}

/**
 * Opens a chapter past the last Section, owning nothing — a holder to add
 * Sections into. It anchors to the Section it was placed behind, so whatever
 * lands after that Section falls into it without a further write.
 *
 * Only one can trail a given Section; a second would be indistinguishable from
 * the first, which is DS6 again in the one place an empty chapter is legal.
 */
export function appendChapterToReel(
  reel: Reel | undefined,
  afterSectionId: string,
  firstSectionId: string,
): Reel {
  const chapters = reel?.chapters ?? [];
  if (chapters.some(chapter => chapter.afterSectionId === afterSectionId)) {
    return {
      ...reel,
      chapters: [...chapters],
      sections: { ...reel?.sections },
    };
  }

  // Always needs the opener when it is the first boundary: a chapter past the
  // last Section owns none of them, so every Section would be orphaned.
  return withChapter(
    reel,
    newChapter({ firstSectionId: '', afterSectionId }),
    firstSectionId,
  );
}

/**
 * Adds a boundary, together with the one at the reel's first Section that DS3
 * requires when this is the first — otherwise the Sections above it would be
 * left outside every chapter.
 */
function withChapter(
  reel: Reel | undefined,
  chapter: ReelChapter,
  openingSectionId: string | undefined,
): Reel {
  const chapters = reel?.chapters ?? [];
  const opening =
    chapters.length === 0 && openingSectionId
      ? [newChapter({ firstSectionId: openingSectionId })]
      : [];

  return {
    ...reel,
    chapters: [...chapters, ...opening, chapter],
    sections: { ...reel?.sections },
  };
}

/**
 * A chapter titled by hand (DE10). `titleSource: 'user'` is what protects it
 * from a later generation (PLAT-11064). Blank clears the title back to the
 * ordinal fallback rather than storing an empty heading (DS5).
 */
export function renameChapterInReel(
  reel: Reel | undefined,
  chapterId: string,
  title: string,
  source: NameSource = 'user',
): Reel {
  const trimmed = title.trim();
  // Cleared back to the ordinal fallback is `'clip'` whoever cleared it — there
  // is no stored title left for a provenance to describe. PLAT-11059 passes
  // `'generated'` for a title the AI wrote (DE10).
  const titleSource: NameSource = trimmed ? source : 'clip';
  return {
    ...reel,
    chapters: (reel?.chapters ?? []).map(chapter =>
      chapter.id === chapterId
        ? { ...chapter, title: trimmed, titleSource }
        : chapter,
    ),
    sections: { ...reel?.sections },
  };
}

/**
 * PLAT-11064 DS2/DS3 — is a Chapter's generated title older than what it holds?
 *
 * `titledFrom` is the Section ids the title was generated from, held for the
 * session only (DS3): persisting a fingerprint would put a cache-invalidation
 * field into `clipJson` for a hint that only matters while the panel is open.
 *
 * A Chapter with no entry — never generated, or generated in an earlier session —
 * makes no claim. The hint offers a re-generation, and offering one against a
 * title nobody here generated would be noise.
 *
 * Order counts as a change: the same Sections rearranged are a different run of
 * the reel, and the name characterises the run.
 */
export function isChapterTitleStale(
  titledFrom: readonly string[] | undefined,
  sectionIds: readonly string[],
): boolean {
  if (!titledFrom) return false;
  if (titledFrom.length !== sectionIds.length) return true;
  return sectionIds.some((id, index) => id !== titledFrom[index]);
}

/**
 * PLAT-11056 — folds a chapter away, or opens it back up.
 *
 * Stored rather than session-local (DS3): a creator who collapsed four chapters
 * to work on the fifth should find it that way when they come back. Expanding
 * drops the flag rather than storing `false`, so the reel only ever carries the
 * chapters that are actually folded.
 */
export function setChapterCollapsed(
  reel: Reel | undefined,
  chapterId: string,
  collapsed: boolean,
): Reel {
  return {
    ...reel,
    chapters: (reel?.chapters ?? []).map(chapter => {
      if (chapter.id !== chapterId) return chapter;
      const { collapsed: _was, ...rest } = chapter;
      return collapsed ? { ...rest, collapsed: true } : rest;
    }),
    sections: { ...reel?.sections },
  };
}

/** Where a dragged Section landed, in chapter terms (PLAT-11075 DS1). */
export type ReelSectionMove = {
  sectionId: string;
  /** The chapter the drop landed in; absent on a reel with no chapters. */
  chapterId?: string;
  /** The Section becomes that chapter's first — the arrival that moves a boundary. */
  opensChapter?: boolean;
};

/**
 * PLAT-11075 — the boundary writes a drag makes. Membership is derived from the
 * row order (DE12), so the usual drop writes nothing at all; exactly two things
 * move a boundary (DS2), and both are applied here against the **pre-move** row
 * order, which is where a departing chapter's next member is read from.
 *
 * - **Departure.** The Section a chapter is anchored to left it, so the chapter
 *   re-anchors to the Section that followed it. With nothing left under it, it
 *   stays where it was as an empty chapter rather than merging away (DS3).
 * - **Arrival.** The Section became a chapter's first, so that chapter
 *   re-anchors to it — without this the Section would slide under the boundary
 *   and belong to the chapter above.
 *
 * Departure runs first, so a Section dropped back at its own chapter's start
 * nets out to no change.
 */
export function moveSectionInReelChapters(
  reel: Reel | undefined,
  rowIds: readonly string[],
  move: ReelSectionMove,
): Reel {
  const chapters = reel?.chapters ?? [];
  const sections = { ...reel?.sections };
  if (chapters.length === 0)
    return { ...reel, chapters: [...chapters], sections };

  // A Section that is not in the pre-move order is arriving rather than moving
  // — an insert (PLAT-11057) landing in an empty chapter — so there is no
  // chapter it departed for it to leave behind.
  const from = rowIds.indexOf(move.sectionId);
  const departed = chapters.map(chapter => {
    if (from === -1 || chapter.firstSectionId !== move.sectionId)
      return chapter;

    const following = rowIds[from + 1];
    const opensAnother =
      following !== undefined &&
      chapters.some(
        other => other.id !== chapter.id && other.firstSectionId === following,
      );
    if (following !== undefined && !opensAnother) {
      return {
        ...chapter,
        firstSectionId: following,
        afterSectionId: undefined,
      };
    }

    // Nothing left under it. It keeps its place as the gap it sat in — after
    // the Section above it, or the top of the reel when there was none (DS4).
    return {
      ...chapter,
      firstSectionId: '',
      afterSectionId: from > 0 ? rowIds[from - 1] : '',
    };
  });

  return {
    ...reel,
    chapters: departed.map(chapter =>
      move.opensChapter && chapter.id === move.chapterId
        ? {
            ...chapter,
            firstSectionId: move.sectionId,
            afterSectionId: undefined,
          }
        : chapter,
    ),
    sections,
  };
}

/**
 * PLAT-11075 DS8 — what an undo takes back from a snapshot of the reel.
 *
 * A snapshot carries the whole clip, so restoring one wholesale would also
 * restore titles typed since (PLAT-11054's reason for carrying the live reel
 * across instead). Boundaries are the opposite case: they are positional, and a
 * drag writes one in the same gesture that moves the timeline, so an undo that
 * left them behind would put the Section back in the wrong chapter.
 *
 * So: live titles, snapshot anchors, and a chapter inserted or removed since
 * the snapshot is left alone — neither is timeline state.
 *
 * PLAT-11072 — which Sections are **off** goes back with the snapshot for the
 * same reason, and more sharply: the entry is written by the same gesture that
 * takes the passage off the timeline, so an undo that kept the live value would
 * put the passage back *and* leave its off entry standing — the row twice over.
 */
export function restoreChapterAnchors(
  live: Reel | undefined,
  snapshot: Reel | undefined,
): Reel | undefined {
  if (!live) return live;

  const anchors = new Map(
    (snapshot?.chapters ?? []).map(chapter => [chapter.id, chapter]),
  );
  const restored: Reel = {
    ...live,
    chapters: (live.chapters ?? []).map(chapter => {
      const was = anchors.get(chapter.id);
      return was
        ? {
            ...chapter,
            firstSectionId: was.firstSectionId,
            afterSectionId: was.afterSectionId,
          }
        : chapter;
    }),
  };

  // Absent rather than empty, so undoing back past the first turn-off leaves
  // the reel as it was before the feature was ever used.
  if (snapshot?.disabledSections?.length) {
    restored.disabledSections = snapshot.disabledSections;
  } else {
    delete restored.disabledSections;
  }

  return restored;
}

/**
 * PLAT-11072 — a chapter that owns nothing, moved.
 *
 * It has no run of Sections to permute, so there is no timeline edit here and
 * no command: its place *is* its anchor, and moving it rewrites that and
 * nothing else (`''` being the gap at the top of the reel, DS4).
 *
 * Only one empty chapter may trail a given Section — the uniqueness
 * {@link appendChapterToReel} keeps, since two holders in one gap are
 * indistinguishable — so a move onto an occupied anchor is refused and the reel
 * comes back untouched.
 */
export function moveEmptyChapterInReel(
  reel: Reel | undefined,
  chapterId: string,
  afterSectionId: string,
): Reel {
  const chapters = reel?.chapters ?? [];
  const moving = chapters.find(chapter => chapter.id === chapterId);
  const isEmpty = moving?.afterSectionId !== undefined;
  const taken = chapters.some(
    chapter =>
      chapter.id !== chapterId && chapter.afterSectionId === afterSectionId,
  );

  if (
    !moving ||
    !isEmpty ||
    moving.afterSectionId === afterSectionId ||
    taken
  ) {
    return {
      ...reel,
      chapters: [...chapters],
      sections: { ...reel?.sections },
    };
  }

  return {
    ...reel,
    chapters: chapters.map(chapter =>
      chapter.id === chapterId ? { ...chapter, afterSectionId } : chapter,
    ),
    sections: { ...reel?.sections },
  };
}

/**
 * PLAT-11146 — whether a write actually moved a boundary.
 *
 * Anchors only, since they are all {@link restoreChapterAnchors} takes back: a
 * write that leaves every one of them alone has nothing for an undo entry to
 * restore, and recording one anyway gives the creator an Undo that appears to
 * do nothing when they reach for it.
 */
export function chapterAnchorsChanged(
  before: Reel | undefined,
  after: Reel | undefined,
): boolean {
  const was = new Map(
    (before?.chapters ?? []).map(chapter => [chapter.id, chapter]),
  );
  const now = after?.chapters ?? [];
  if (was.size !== now.length) return true;

  return now.some(chapter => {
    const previous = was.get(chapter.id);
    return (
      !previous ||
      previous.firstSectionId !== chapter.firstSectionId ||
      previous.afterSectionId !== chapter.afterSectionId
    );
  });
}

/**
 * Drops a boundary; its Sections merge into the chapter above (DS4). Removing
 * the only chapter returns the reel to the flat list.
 */
export function removeChapterFromReel(
  reel: Reel | undefined,
  chapterId: string,
): Reel {
  return {
    ...reel,
    chapters: (reel?.chapters ?? []).filter(
      chapter => chapter.id !== chapterId,
    ),
    sections: { ...reel?.sections },
  };
}

/**
 * PLAT-11204 — every boundary at once, returning the reel to the flat list it
 * started as (PLAT-11055 DS2).
 *
 * One write rather than {@link removeChapterFromReel} in a loop (DS1): each step
 * of a loop is a state the panel would derive and save, and the derivation
 * re-anchors the opening boundary onto whichever chapter is left (PLAT-11055
 * DS3) — so the creator would watch the list walk down to empty.
 */
export function removeAllChaptersFromReel(reel: Reel | undefined): Reel {
  return {
    ...reel,
    chapters: [],
    sections: { ...reel?.sections },
  };
}
